import axios from 'axios';
import { baseUrl } from './baseUrls';

import toast from 'react-hot-toast';

/**
 * Centralized API Client – Cookie-based auth
 *
 * - Auth is via HTTP-only cookies (access + refresh). No tokens in localStorage.
 * - Only user, permissions, and roles are stored in localStorage for RBAC/UI.
 * - withCredentials: true so cookies are sent and received.
 * - On 401, refresh is attempted via backend (cookie); on failure, clear user and redirect to login.
 */

const USER_DATA_STORAGE_KEY = 'user_data';
const LOGIN_REDIRECT_PATH = '/login';

/**
 * User/session data in localStorage (no tokens – those are in cookies).
 */
class TokenManager {
  static getUserData() {
    try {
      const raw = localStorage.getItem(USER_DATA_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  static setUserData(userData) {
    try {
      if (userData) {
        localStorage.setItem(USER_DATA_STORAGE_KEY, JSON.stringify(userData));
      } else {
        localStorage.removeItem(USER_DATA_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error setting user data:', error);
    }
  }

  /**
   * Store auth data from login response.
   * @param {object} data - response.data from login (e.g. { user: { uuid, name, email, roles, permissions } })
   */
  static setAuthData(data) {
    try {
      const user = data?.user;
      if (!user) return;
      const payload = {
        user,
        permissions: user.permissions ?? [],
        roles: user.roles ?? [],
      };
      this.setUserData(payload);
    } catch (error) {
      console.error('Error storing authentication data:', error);
    }
  }

  static clearUserData() {
    try {
      localStorage.removeItem(USER_DATA_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing user data:', error);
    }
  }

  static clearAllAuthData() {
    this.clearUserData();
  }

  /** Authenticated if we have stored user data (cookies are httpOnly and not readable here). */
  static isAuthenticated() {
    return !!this.getUserData();
  }
}

/**
 * API Client Class
 * Main class that encapsulates all API functionality with token management
 * 
 * MULTIPLE BASE URL SUPPORT:
 * - Relative URLs (e.g., '/users') use the configured baseURL
 * - Absolute URLs (e.g., 'https://other-api.com/users') override the baseURL
 */
class ApiClient {
  constructor(baseURL = baseUrl) {
    // Cookie-based auth: send and receive cookies (access + refresh tokens are httpOnly)
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      withCredentials: true,
    });

    // Store the default base URL for reference
    this.defaultBaseURL = baseURL;

    // Flag to prevent multiple simultaneous refresh attempts
    this.isRefreshing = false;
    // Queue to store failed requests during token refresh
    this.failedQueue = [];

    // Set up interceptors
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Check if a URL is absolute
   * @param {string} url - URL to check
   * @returns {boolean} True if URL is absolute
   */
  isAbsoluteURL(url) {
    return /^https?:\/\//i.test(url);
  }

  /**
   * Get the effective base URL for a given URL
   * @param {string} url - The URL to analyze
   * @returns {string} The base URL that will be used
   */
  getEffectiveBaseURL(url) {
    return this.isAbsoluteURL(url) ? 'OVERRIDDEN' : this.defaultBaseURL;
  }

  /**
   * Request Interceptor
   * Auth is via cookies (withCredentials: true); no Authorization header needed.
   */
  setupRequestInterceptor() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Returns true if the request is to the login endpoint (invalid credentials 401).
   */
  isLoginRequest(config) {
    const url = (config?.url || '').toString();
    return url.includes('/api/auth/login') || /\/auth\/login\/?$/i.test(url);
  }

  /**
   * Returns true if the request is to the refresh endpoint (session expired 401).
   * Matches POST /api/auth/refresh (cookie-based refresh).
   */
  isRefreshRequest(config) {
    const url = (config?.url || '').toString();
    return url.includes('refresh-token') || /\/auth\/refresh-token\/?$/i.test(url) || /\/api\/auth\/refresh\/?$/i.test(url);
  }

  /**
   * Returns true if the request is to the logout endpoint (do not trigger session-expired flow on 401).
   */
  isLogoutRequest(config) {
    const url = (config?.url || '').toString();
    return url.includes('/api/auth/logout') || /\/auth\/logout\/?$/i.test(url);
  }

  /**
   * Response Interceptor
   * Distinguishes: login 401 (pass through) | logout 401 (pass through) | refresh 401 (session expired) | protected 401 (try refresh + retry).
   */
  setupResponseInterceptor() {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if (status !== 401) {
          return Promise.reject(error);
        }

        // Case 1: Login API 401 – invalid credentials. Do not logout, redirect, or show session toast.
        if (this.isLoginRequest(originalRequest)) {
          return Promise.reject(error);
        }

        // Logout API 401 – e.g. no valid cookie; just reject so caller's finally (clearAuth) runs.
        if (this.isLogoutRequest(originalRequest)) {
          return Promise.reject(error);
        }

        // Case 2: Refresh API 401 – session expired. Clear auth, show toast, redirect.
        if (this.isRefreshRequest(originalRequest)) {
          this.handleAuthenticationFailure();
          return Promise.reject(error);
        }

        // Case 3: Protected API 401 – access token expired. Try refresh, then retry.
        if (originalRequest._retry) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;

        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          }).then(() => this.axiosInstance(originalRequest));
        }

        this.isRefreshing = true;
        try {
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            this.processQueue(null);
            return this.axiosInstance(originalRequest);
          }
          this.handleAuthenticationFailure();
          return Promise.reject(error);
        } catch (refreshError) {
          this.processQueue(refreshError);
          // Refresh 401 already handled in interceptor; avoid duplicate toast/redirect
          if (!this.isRefreshRequest(refreshError?.config)) {
            this.handleAuthenticationFailure();
          }
          return Promise.reject(refreshError);
        } finally {
          this.isRefreshing = false;
        }
      }
    );
  }

  /**
   * Refresh access token via backend (refresh token in httpOnly cookie).
   * API: POST /api/auth/refresh, Cookie: refresh_token=...; token=...
   * Response: { success: true, message: "Token Refreshed" }; new tokens via Set-Cookie.
   * @returns {Promise<boolean>} true if refresh succeeded
   */
  async refreshAccessToken() {
    try {
      await this.axiosInstance.post('/api/auth/refresh', {}, { withCredentials: true });
      return true;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        TokenManager.clearAllAuthData();
      }
      return false;
    }
  }

  /**
   * Process Queued Requests
   * Resolves or rejects all queued requests after token refresh attempt
   * @param {Error|null} error - Error if token refresh failed, null if successful
   */
  processQueue(error) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });
    
    // Clear the queue
    this.failedQueue = [];
  }

  /**
   * Called when token refresh fails: clear user data and redirect to login.
   */
  handleAuthenticationFailure() {
    toast.error('Session expired, please login again', { duration: 5000, position: 'top-center' });
    TokenManager.clearAllAuthData();
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.location.href = LOGIN_REDIRECT_PATH;
      }
    }, 6000);
  }

  /**
   * Store auth data from login response (user, permissions, roles only; tokens are in cookies).
   * @param {object} data - response.data from login (e.g. { user: { ... } })
   */
  setAuthData(data) {
    TokenManager.setAuthData(data);
  }

  /**
   * Get User Data
   * Get stored user data
   * @returns {object|null} User data or null if not found
   */
  getUserData() {
    return TokenManager.getUserData();
  }

  /**
   * Clear stored user/permissions/roles (cookies are cleared by browser on logout or by backend).
   */
  clearAuth() {
    TokenManager.clearAllAuthData();
  }

  /**
   * Whether we have stored user data (treat as authenticated; cookies are httpOnly).
   */
  isAuthenticated() {
    return TokenManager.isAuthenticated();
  }

  /**
   * Get Default Base URL
   * @returns {string} The default base URL
   */
  getDefaultBaseURL() {
    return this.defaultBaseURL;
  }

  /**
   * Update Base URL
   * Change the default base URL for future relative requests
   * @param {string} newBaseURL - New base URL
   */
  updateBaseURL(newBaseURL) {
    this.axiosInstance.defaults.baseURL = newBaseURL;
    this.defaultBaseURL = newBaseURL;
  }

  // HTTP Method Wrappers
  // These methods provide clean interfaces for making API calls
  // They support both relative URLs (using baseURL) and absolute URLs (overriding baseURL)

  /**
   * GET Request
   * @param {string} url - The endpoint URL (relative or absolute)
   * @param {object} config - Additional axios configuration
   * @returns {Promise} Axios response promise
   * 
   * Examples:
   * - get('/users') - Uses baseURL + '/users'
   * - get('https://api2.example.com/users') - Uses absolute URL, ignores baseURL
   */
  async get(url, config = {}) {
    try {
      const response = await this.axiosInstance.get(url, config);
      return response;
    } catch (error) {
      console.error(`GET ${url} failed:`, error);
      throw error;
    }
  }

  /**
   * POST Request
   * @param {string} url - The endpoint URL (relative or absolute)
   * @param {object} data - Request payload
   * @param {object} config - Additional axios configuration
   * @returns {Promise} Axios response promise
   */
  async post(url, data = {}, config = {}) {
    try {
      const response = await this.axiosInstance.post(url, data, config);
      return response;
    } catch (error) {
      console.error(`POST ${url} failed:`, error);
      throw error;
    }
  }

  /**
   * PUT Request
   * @param {string} url - The endpoint URL (relative or absolute)
   * @param {object} data - Request payload
   * @param {object} config - Additional axios configuration
   * @returns {Promise} Axios response promise
   */
  async put(url, data = {}, config = {}) {
    try {
      const response = await this.axiosInstance.put(url, data, config);
      return response;
    } catch (error) {
      console.error(`PUT ${url} failed:`, error);
      throw error;
    }
  }

  /**
   * PATCH Request
   * @param {string} url - The endpoint URL (relative or absolute)
   * @param {object} data - Request payload
   * @param {object} config - Additional axios configuration
   * @returns {Promise} Axios response promise
   */
  async patch(url, data = {}, config = {}) {
    try {
      const response = await this.axiosInstance.patch(url, data, config);
      return response;
    } catch (error) {
      console.error(`PATCH ${url} failed:`, error);
      throw error;
    }
  }

  /**
   * DELETE Request
   * @param {string} url - The endpoint URL (relative or absolute)
   * @param {object} config - Additional axios configuration
   * @returns {Promise} Axios response promise
   */
  async delete(url, config = {}) {
    try {
      const response = await this.axiosInstance.delete(url, config);
      return response;
    } catch (error) {
      console.error(`DELETE ${url} failed:`, error);
      throw error;
    }
  }

  /**
   * Generic Request Method
   * For more complex requests that need full axios configuration
   * @param {object} config - Full axios configuration object
   * @returns {Promise} Axios response promise
   */
  async request(config) {
    try {
      const response = await this.axiosInstance.request(config);
      return response;
    } catch (error) {
      console.error('Custom request failed:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient();

// Export both the instance and the class for flexibility
export default apiClient;
export { ApiClient, TokenManager };
